<template>
  <div class="dashboard" style="max-width: 100%; padding: 4em 2em 1em;" v-if="isReady">
    <div id="breadcrumbs">
      <div>
        <div style="display: flex;">
          <router-link :to="link" :aria-label="$t('files.home')" :title="$t('files.home')">
            <i class="material-icons">folder</i>
          </router-link>

          <span v-for="(link, index) in bclist" :key="index">
            <span class="chevron"><i class="material-icons">keyboard_arrow_right</i></span>
            <router-link :to="link.url">{{ link.name }}</router-link>
          </span>
        </div>
        <div class="buttons" v-if="isMobile">
          <new-button v-if="$route.hash !== '' && permissions === 0 && permissionsSharedDrive === 0"></new-button>
          <upload-button v-if="$route.hash !== '' && permissions === 0 && permissionsSharedDrive === 0"></upload-button>
        </div>
      </div>
      <switch-button v-show="isListing"></switch-button>
    </div>
    <div v-if="error">
      <not-found v-if="error.message === '404'"></not-found>
      <forbidden v-else-if="error.message === '403'"></forbidden>
      <internal-error v-else></internal-error>
    </div>
    <editor v-else-if="isEditor"></editor>
    <preview v-else-if="isPreview"></preview>
    <listing-shared-drive :class="{ multiple }" v-else-if="!loading"></listing-shared-drive>
    <div v-else>
      <h2 class="message">
        <span>{{ $t('files.loading') }}</span>
      </h2>
    </div>
  </div>
</template>

<script>
import Forbidden from './errors/403';
import NotFound from './errors/404';
import InternalError from './errors/500';
import Preview from '@/components/files/Preview.vue';
import ListingSharedDrive from '@/components/files/ListingSharedDrive.vue';
import Editor from '@/components/files/Editor.vue';
import SwitchButton from '@/components/buttons/SwitchView.vue';
import { files as api } from '@/api';
import { mapGetters, mapState, mapMutations } from 'vuex';
import UploadButton from '@/components/buttons/Upload.vue';
import NewButton from '@/components/buttons/New.vue';

export default {
  name: 'shared-with-me',
  components: {
    Forbidden,
    NotFound,
    InternalError,
    Preview,
    ListingSharedDrive,
    Editor,
    SwitchButton,
    UploadButton,
    NewButton
  },
  computed: {
    ...mapGetters([
      'selectedCount',
      'isListing',
      'isEditor',
      'isFiles'
    ]),
    ...mapState([
      'req',
      'user',
      'reload',
      'multiple',
      'loading',
      'sortOrder',
      'isDelete',
      'isRename',
      'permissions',
      'permissionsSharedDrive',
      'linkSharedDrive'
    ]),
    isPreview () {
      return !this.loading && !this.isEditor && !this.flag;
    },
    isMobile () {
      return window.innerWidth <= 768;
    },
  },
  data: function () {
    return {
      error: null,
      bclist: [],
      flag: true,
      isReady: false,
      link: ''
    };
  },
  created () {
    let thisComponent = this;
    let JavaEventListenerClass = Module.ActionListener.extend('ActionListener', {
      onActionFinish: function(action)
      {
        if (action.type === Module.ActionType.MANAGE_SHARED_DRIVES.value && action.node !== undefined) {
          thisComponent.fetchData();
        } else {
          return;
        }
      }
    });
    this.interfaceObject = new JavaEventListenerClass();
    ibb.addActionListener(this.interfaceObject);
    this.link = window.location.pathname + window.location.hash;
  },
  watch: {
    '$route': 'fetchData',
    'reload': function () {
      this.fetchData();
    }
  },
  mounted () {
    window.addEventListener('keydown', this.keyEvent);
    window.addEventListener('scroll', this.scroll);
    this.fetchData();
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.keyEvent);
    window.removeEventListener('scroll', this.scroll);
    ibb.removeActionListener(this.interfaceObject);
  },
  destroyed () {
    this.$store.commit('updateRequest', {});
  },
  methods: {
    ...mapMutations([ 'setLoading', 'setLinkSharedDrive' ]),
    async fetchData () {
      // Reset view information.
      this.$store.commit('setReload', false);
      this.$store.commit('resetSelected');
      this.$store.commit('multiple', false);
      //this.$store.commit('closeHovers')

      // Set loading to true and reset the error.
      this.setLoading(true);
      this.error = null;

      let url = this.$route.hash.slice(1);
      
      try {
        let res;
        if (url.length > 8) {
          let action = await ibbLink.getRootNode(0);
          res = await api.fetchId(action.node.handle, this.user.viewMode == 'list' ? this.sortOrder : Module.SortOrderType.SORT_ORDER_DEFAULT.value, ibbLink);
          this.flag = true;
          this.setLinkSharedDrive(url);
        } else {
          res = await api.fetchId(url, this.user.viewMode == 'list' ? this.sortOrder : Module.SortOrderType.SORT_ORDER_DEFAULT.value, ibbLink);
          if (res.isDir === false) {
            this.flag = false;
          } else {
            this.flag = true;
          }
        }

        this.$store.commit('updateRequest', res);
        this.isReady = true;

        let breadcrumbs = [];
        let folder = res;
        while (folder.parentHandle) {
          breadcrumbs.unshift({ name: decodeURIComponent(folder.name), url: this.$route.path + '#' + folder.handle });
          folder = await api.getParent(folder.parentHandle, ibbLink);
        }
        breadcrumbs.unshift({ name: decodeURIComponent(folder.name), url: this.$route.path + '#' + this.linkSharedDrive});
        
        if (breadcrumbs.length > 3) {
          while (breadcrumbs.length !== 4) {
            breadcrumbs.shift();
          }

          breadcrumbs[0].name = '...';
        }
        this.bclist = breadcrumbs;
        document.title = res.name;
      } catch (e) {
        this.error = e;
      } finally {
        this.setLoading(false);
      }
    },
    keyEvent (event) {
      // Esc!
      if (event.keyCode === 27) {
        this.$store.commit('closeHovers');

        // If we're on a listing, unselect all
        // files and folders.
        if (this.isListing) {
          this.$store.commit('resetSelected');
        }
      }

      // Del!
      if (event.keyCode === 46) {
        if (this.isEditor ||
          !this.isFiles ||
          this.loading ||
          !this.user.perm.delete ||
          (this.isListing && this.selectedCount === 0) || this.isDelete) return; 

        this.$store.commit('showHover', 'delete');
      }

      // F1!
      if (event.keyCode === 112) {
        event.preventDefault();
        this.$store.commit('showHover', 'help');
      }

      // F2!
      if (event.keyCode === 113) {
        if (this.isEditor ||
          !this.isFiles ||
          this.loading ||
          !this.user.perm.rename ||
          (this.isListing && this.selectedCount === 0) ||
          (this.isListing && this.selectedCount > 1) || this.isRename) return;

        this.$store.commit('showHover', 'rename');
      }

      // CTRL + S
      if (event.ctrlKey || event.metaKey) {
        if (this.isEditor) return;

        if (String.fromCharCode(event.which).toLowerCase() === 's') {
          event.preventDefault();

          if (this.req.kind !== 'editor') {
            document.getElementById('download-button').click();
          }
        }
      }
    },
    scroll () {
      if (this.req.kind !== 'listing' || this.$store.state.user.viewMode === 'mosaic') return;

      let top = 112 - window.scrollY;

      if (top < 64) {
        top = 64;
      }

      document.querySelector('#listing.list .item.header').style.top = top + 'px';
    },
    openSidebar () {
      this.$store.commit('showHover', 'sidebar');
    },
    openSearch () {
      this.$store.commit('showHover', 'search');
    },
    copy () {
      this.$store.commit('showHover', 'share');
    }
  }
};
</script>